import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationAPI } from '../services/api';
import { pusherService } from '../services/pusherService';
import './NotificationSystem.css';

const NotificationSystem = ({ isBottomNav = false }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pusher, setPusher] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Cache tracking for performance optimization
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 30000; // 30 seconds cache
  
  // Active only when dropdown is open (not based on route)
  const isActive = showDropdown;

  // Fetch notifications from API with caching
  const fetchNotifications = async (forceRefresh = false) => {
    if (!isAuthenticated || !user) return;

    // Check cache - if data is fresh and not forcing refresh, skip API call
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    if (!forceRefresh && timeSinceLastFetch < CACHE_DURATION && notifications.length > 0) {
      console.log('[NotificationSystem] Using cached notifications (fresh data)');
      return;
    }

    try {
      setLoading(true);
      const response = await notificationAPI.getAll({ per_page: 10 });
      
      if (response.success && response.data) {
        const fetchedNotifications = response.data.data || response.data || [];
        
        console.log('[NotificationSystem] Fetched notifications from API:', fetchedNotifications.length);
        
        // Transform API notifications to match our format
        const transformedNotifications = fetchedNotifications.map(notif => ({
          id: notif.id,
          type: notif.type,
          title: notif.title || 'Notification',
          message: notif.message || '',
          jobId: notif.data?.job_id,
          jobToken: notif.data?.share_token, // Add share_token for secure redirect
          jobTitle: notif.data?.job_title,
          reason: notif.data?.reason,
          adminName: notif.data?.admin_name,
          forumId: notif.data?.forum_id,
          schedule_id: notif.data?.schedule_id,
          template_id: notif.data?.template_id,
          applicationId: notif.data?.application_id, // For job_application notifications
          applicantId: notif.data?.applicant_id, // For job_application notifications
          applicantName: notif.data?.applicant_name, // For job_application notifications
          status: notif.data?.status, // For job_application_status notifications
          rejectionReason: notif.data?.rejection_reason, // For rejected applications
          timestamp: new Date(notif.created_at),
          unread: !notif.is_read,
          notificationId: notif.id, // Store the real notification ID from DB
        }));

        console.log('[NotificationSystem] Transformed notifications:', transformedNotifications.length);
        // Debug: Log notification types to verify
        if (transformedNotifications.length > 0) {
          console.log('[NotificationSystem] Notification types:', transformedNotifications.map(n => ({ id: n.id, type: n.type, title: n.title })));
        }

        setNotifications(transformedNotifications);
        lastFetchTimeRef.current = now; // Update cache timestamp
        
        // Update unread count
        const unread = transformedNotifications.filter(n => n.unread).length;
        setUnreadCount(unread);
        console.log('[NotificationSystem] Unread count:', unread);
      }
    } catch (error) {
      console.error('[NotificationSystem] Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await notificationAPI.getUnreadCount();
      if (response.success && response.count !== undefined) {
        setUnreadCount(response.count);
      }
    } catch (error) {
      console.error('[NotificationSystem] Error fetching unread count:', error);
    }
  };

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      console.log('[NotificationSystem] User not authenticated or not available');
      return;
    }

    // Fetch notifications from API
    fetchNotifications();
    
    // Also fetch unread count separately
    fetchUnreadCount();

    // Refresh notifications when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications(false); // Use cache if available
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Use Pusher service - ensure it's initialized
    const pusherInstance = pusherService.getPusher();
    
    if (!pusherInstance) {
      console.log('[NotificationSystem] Pusher not initialized, initializing now...');
      pusherService.initialize(user.id, user.role);
      setPusher(pusherService.getPusher());
    } else {
      setPusher(pusherInstance);
    }

    // Listen to Pusher events from service
    const handleAnnouncement = (event) => {
      console.log('[NotificationSystem] 📢 Announcement event received:', event);
      console.log('[NotificationSystem] Event detail:', event.detail);
      const data = event.detail;
      
      if (!data) {
        console.error('[NotificationSystem] ❌ No data in announcement event');
        return;
      }
      
      console.log('[NotificationSystem] Adding announcement notification with data:', {
        notification_id: data.notification_id,
        title: data.title,
        message: data.message,
        schedule_id: data.schedule_id,
      });
      
      // Create notification object with all available data
      // Use type from data if available, otherwise default to 'announcement'
      const notificationType = data.type || 'announcement';
      
      const notification = {
        id: data.notification_id || `announcement-${Date.now()}`,
        type: notificationType,
        title: data.title || 'New Announcement',
        message: data.message || '',
        timestamp: new Date(),
        unread: true,
        notificationId: data.notification_id,
        schedule_id: data.schedule_id,
        template_id: data.template_id,
      };
      
      console.log('[NotificationSystem] Created notification with type:', notificationType);
      
      console.log('[NotificationSystem] Notification object created:', notification);
      addNotification(notification);
    };

    const handleNotification = (event) => {
      console.log('[NotificationSystem] 🔔 Notification event received:', event.detail);
      const data = event.detail;
      addNotification({
        id: data.notification_id || Date.now(),
        type: data.type || 'notification',
        title: data.title || 'New Notification',
        message: data.message || '',
        timestamp: new Date(),
        unread: true,
        notificationId: data.notification_id,
      });
      fetchUnreadCount();
    };

    // Handle existing forum notifications
    const handleForumStatusUpdate = (event) => {
      const data = event.detail;
      addNotification({
        id: Date.now(),
        type: 'status_update',
        title: 'Forum Status Updated',
        message: data.message,
        forumId: data.forum_id,
        timestamp: new Date(),
        unread: true
      });
      fetchUnreadCount();
    };

    const handleAdminReply = (event) => {
      const data = event.detail;
      addNotification({
        id: Date.now(),
        type: 'admin_reply',
        title: 'Admin Reply',
        message: data.message,
        forumId: data.forum_id,
        timestamp: new Date(),
        unread: true
      });
      fetchUnreadCount();
    };

    const handleJobCompliance = (event) => {
      const data = event.detail;
      console.log('[NotificationSystem] 📬 Received job-compliance notification:', data);
      addNotification({
        id: data.notification_id || Date.now(),
        notificationId: data.notification_id || null,
        type: 'job_compliance',
        title: data.title || 'Job Marked as Non-Compliant',
        message: data.message,
        jobId: data.job_id,
        jobToken: data.share_token,
        jobTitle: data.job_title,
        reason: data.reason,
        adminName: data.admin_name,
        timestamp: new Date(data.removed_at || Date.now()),
        unread: true
      });
      setTimeout(() => {
        fetchNotifications(true); // Force refresh for job compliance
        fetchUnreadCount();
      }, 500);
    };

    const handleJobApplication = (event) => {
      const data = event.detail;
      console.log('[NotificationSystem] 📬 Received job-application notification:', data);
      addNotification({
        id: data.notification_id || Date.now(),
        notificationId: data.notification_id || null,
        type: 'job_application',
        title: data.title || 'New Job Application',
        message: data.message,
        jobId: data.job_id,
        jobToken: data.share_token,
        jobTitle: data.job_title,
        applicationId: data.application_id,
        applicantId: data.applicant_id,
        applicantName: data.applicant_name,
        timestamp: new Date(data.applied_at || Date.now()),
        unread: true
      });
      setTimeout(() => {
        fetchNotifications(true); // Force refresh for job application
        fetchUnreadCount();
      }, 500);
    };

    const handleJobApplicationStatus = (event) => {
      const data = event.detail;
      console.log('[NotificationSystem] 📬 Received job-application-status notification:', data);
      addNotification({
        id: data.notification_id || Date.now(),
        notificationId: data.notification_id || null,
        type: 'job_application_status',
        title: data.title || 'Application Status Updated',
        message: data.message,
        jobId: data.job_id,
        jobToken: data.share_token,
        jobTitle: data.job_title,
        applicationId: data.application_id,
        status: data.status,
        rejectionReason: data.rejection_reason,
        timestamp: new Date(data.applied_at || Date.now()),
        unread: true
      });
      setTimeout(() => {
        fetchNotifications(true); // Force refresh for job application status
        fetchUnreadCount();
      }, 500);
    };

    const handleAdminNotification = (event) => {
      const data = event.detail;
      addNotification({
        id: data.notification_id || Date.now(),
        type: data.type || 'admin_notification',
        title: data.title || 'Admin Notification',
        message: data.message || '',
        timestamp: new Date(),
        unread: true,
        notificationId: data.notification_id,
      });
      fetchUnreadCount();
    };

    const handleNewForumPost = (event) => {
      const data = event.detail;
      addNotification({
        id: Date.now(),
        type: 'new_forum',
        title: 'New Forum Question',
        message: `New question from ${data.user_name}: ${data.title}`,
        forumId: data.forum_id,
        timestamp: new Date(),
        unread: true
      });
      fetchUnreadCount();
    };

    // Add event listeners immediately (synchronously)
    window.addEventListener('pusher:announcement', handleAnnouncement);
    window.addEventListener('pusher:notification', handleNotification);
    window.addEventListener('pusher:forum-status-updated', handleForumStatusUpdate);
    window.addEventListener('pusher:admin-reply', handleAdminReply);
    window.addEventListener('pusher:job-compliance', handleJobCompliance);
    window.addEventListener('pusher:job-application', handleJobApplication);
    window.addEventListener('pusher:job-application-status', handleJobApplicationStatus);
    
    if (user.role === 'admin' || user.is_admin) {
      window.addEventListener('pusher:admin-notification', handleAdminNotification);
      window.addEventListener('pusher:job-report', handleAdminNotification);
      window.addEventListener('pusher:new-forum-post', handleNewForumPost);
    }

    console.log('[NotificationSystem] ✅ Event listeners registered for Pusher events');
    console.log('[NotificationSystem] Pusher connection state:', pusherService.getIsConnected() ? 'Connected' : 'Not Connected');
    console.log('[NotificationSystem] Pusher instance:', pusherInstance ? 'Exists' : 'Missing');
    console.log('[NotificationSystem] User channel:', pusherService.getUserChannel() ? 'Subscribed' : 'Not Subscribed');

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Remove event listeners
      window.removeEventListener('pusher:announcement', handleAnnouncement);
      window.removeEventListener('pusher:notification', handleNotification);
      window.removeEventListener('pusher:forum-status-updated', handleForumStatusUpdate);
      window.removeEventListener('pusher:admin-reply', handleAdminReply);
      window.removeEventListener('pusher:job-compliance', handleJobCompliance);
      window.removeEventListener('pusher:job-application', handleJobApplication);
      window.removeEventListener('pusher:job-application-status', handleJobApplicationStatus);
      
      if (user.role === 'admin' || user.is_admin) {
        window.removeEventListener('pusher:admin-notification', handleAdminNotification);
        window.removeEventListener('pusher:job-report', handleAdminNotification);
        window.removeEventListener('pusher:new-forum-post', handleNewForumPost);
      }
      
      // Don't disconnect Pusher here - let it stay connected for the session
      // PusherService will handle disconnection on logout
    };
  }, [isAuthenticated, user]);

  const addNotification = (notification) => {
    console.log('[NotificationSystem] Adding notification:', notification);
    
    // Check if notification already exists (by notification_id or by type and key data)
    setNotifications(prev => {
      // First check by notification_id (most reliable)
      if (notification.notificationId) {
        const existsById = prev.some(n => n.notificationId === notification.notificationId);
        if (existsById) {
          console.log('[NotificationSystem] Notification already exists by ID:', notification.notificationId);
          return prev; // Don't add duplicates
        }
      }
      
      // Then check by type and key data (for notifications without ID yet)
      const exists = prev.some(n => {
        // For all announcement types, check by notification_id, schedule_id, or title+message
        const isAnnouncement = notification.type === 'announcement' || 
                               notification.type === 'pusher_announcement' || 
                               notification.type === 'email_announcement';
        
        if (isAnnouncement) {
          if (notification.notificationId && n.notificationId === notification.notificationId) return true;
          if (notification.schedule_id && n.schedule_id === notification.schedule_id) return true;
          if (notification.title === n.title && notification.message === n.message) return true;
        }
        
        // For other types, check by jobId or forumId
        if (notification.jobId && n.jobId === notification.jobId) return true;
        if (notification.forumId && n.forumId === notification.forumId) return true;
        
        return false;
      });
      
      if (exists) {
        console.log('[NotificationSystem] Notification already exists by type/key');
        return prev; // Don't add duplicates
      }
      
      console.log('[NotificationSystem] Adding new notification to state');
      return [notification, ...prev.slice(0, 49)]; // Keep last 50 notifications
    });
    
    setUnreadCount(prev => prev + 1);
    
    // Also refresh from API to get the real notification ID and ensure sync
    setTimeout(() => {
      fetchNotifications(true); // Force refresh after adding new notification
      fetchUnreadCount();
    }, 1000);
  };

  const markAsRead = async (notificationId) => {
    // Find the notification to get the real DB ID
    const notification = notifications.find(n => n.id === notificationId || n.notificationId === notificationId);
    
    if (notification && notification.notificationId) {
      try {
        await notificationAPI.markAsRead(notification.notificationId);
      } catch (error) {
        console.error('[NotificationSystem] Error marking notification as read:', error);
      }
    }

    // Update local state
    setNotifications(prev => 
      prev.map(notif => 
        (notif.id === notificationId || notif.notificationId === notificationId) 
          ? { ...notif, unread: false } 
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
    } catch (error) {
      console.error('[NotificationSystem] Error marking all as read:', error);
    }

    // Update local state
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, unread: false }))
    );
    setUnreadCount(0);
  };

  const handleNotificationItemClick = (notification) => {
    // Debug: Log notification details to verify type
    console.log('[NotificationSystem] Notification clicked:', {
      id: notification.id,
      notificationId: notification.notificationId,
      type: notification.type,
      title: notification.title,
    });
    
    // Mark as read using the correct ID
    const notificationId = notification.notificationId || notification.id;
    if (notificationId) {
      markAsRead(notificationId);
    }
    
    // Navigate based on notification type
    // Handle all announcement-related types: 'announcement', 'pusher_announcement', 'email_announcement'
    if (notification.type === 'announcement' || 
        notification.type === 'pusher_announcement' || 
        notification.type === 'email_announcement') {
      // Redirect all announcement notifications to the notifications page
      console.log('[NotificationSystem] Redirecting announcement notification to /notifications');
      setShowDropdown(false);
      navigate('/notifications');
    } else if (notification.forumId) {
      navigate(`/forum/${notification.forumId}`);
    } else if (notification.type === 'job_compliance' && notification.jobToken) {
      // Navigate to job detail page using shareable token (secure, hides internal IDs)
      navigate(`/job/${notification.jobToken}`);
    } else if (notification.type === 'job_compliance' && notification.jobId) {
      // Fallback to job ID if token is not available (for backward compatibility)
      navigate(`/my-jobs?job=${notification.jobId}`);
    } else if (notification.type === 'job_application' && notification.jobToken) {
      // Navigate to my-jobs page using shareable token (secure, hides internal IDs)
      // Add tab=applications to automatically show the applications tab
      navigate(`/my-jobs?job=${notification.jobToken}&tab=applications`);
    } else if (notification.type === 'job_application' && notification.jobId) {
      // Fallback to job ID if token is not available (for backward compatibility)
      navigate(`/my-jobs?job=${notification.jobId}&tab=applications`);
    } else if (notification.type === 'job_application_status' && notification.jobToken) {
      // Navigate to applied jobs page or job detail page for applicants
      navigate(`/applied-jobs`);
    } else if (notification.type === 'job_application_status' && notification.jobId) {
      // Fallback to job ID if token is not available
      navigate(`/applied-jobs`);
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (!isAuthenticated) return null;

  // Handle click - navigate to notifications page on mobile, show dropdown on desktop
  const handleNotificationClick = () => {
    if (isBottomNav) {
      // On mobile (bottom nav), navigate to notifications page
      navigate('/notifications');
    } else {
      // On desktop, toggle dropdown
      const newState = !showDropdown;
      setShowDropdown(newState);
      // Refresh notifications when opening dropdown (with cache check)
      if (newState) {
        // Fetch in background if cache is stale, but show cached data immediately
        fetchNotifications(false); // Use cache if available
        fetchUnreadCount();
      }
    }
  };

  return (
    <div className={`notification-system ${isBottomNav ? 'notification-system-bottom-nav' : ''}`}>
      <button 
        className={`notification-btn ${isBottomNav ? 'notification-btn-bottom-nav' : ''} ${isActive ? 'active' : ''} ${isBottomNav && location.pathname === '/notifications' ? 'active' : ''}`}
        onClick={handleNotificationClick}
        title="Notifications"
      >
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <svg width={isBottomNav ? "22" : "18"} height={isBottomNav ? "22" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          {unreadCount > 0 && (
            <span className={`notification-badge ${isBottomNav ? 'notification-badge-bottom-nav' : ''}`}>{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </div>
        {isBottomNav && <span className="notification-label">Notifications</span>}
      </button>

      {!isBottomNav && showDropdown && (
        <div className={`notification-dropdown ${isBottomNav ? 'notification-dropdown-bottom' : ''}`}>
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button 
                  className="mark-all-read-btn"
                  onClick={markAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>
          
          <div className="notification-list">
            {loading ? (
              <div className="no-notifications">
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification, index) => (
                <div 
                  key={notification.notificationId || notification.id || `notif-${index}`}
                  className={`notification-item ${notification.unread ? 'unread' : ''}`}
                  onClick={() => handleNotificationItemClick(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">
                      {notification.message}
                      {notification.type === 'job_application_status' && notification.status === 'rejected' && notification.rejectionReason && (
                        <div style={{ marginTop: '8px', padding: '8px', background: '#fff3cd', borderRadius: '4px', fontSize: '12px', color: '#856404' }}>
                          <strong>Reason:</strong> {notification.rejectionReason.split('\n\n')[0]}
                        </div>
                      )}
                    </div>
                    <div className="notification-time">{formatTime(notification.timestamp)}</div>
                  </div>
                  {notification.unread && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>
          
          <div className="notification-footer">
            <button 
              className="see-all-notifications-btn"
              onClick={() => {
                setShowDropdown(false);
                navigate('/notifications');
              }}
            >
              See All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;
